import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

type RequestType =
  | "acesso"
  | "correcao"
  | "exclusao"
  | "portabilidade"
  | "revogar_consentimento"
  | "outro";

const requestTypeLabels: Record<RequestType, string> = {
  acesso: "Acessar meus dados",
  correcao: "Corrigir dados pessoais",
  exclusao: "Excluir meus dados",
  portabilidade: "Portabilidade dos dados",
  revogar_consentimento: "Revogar consentimento",
  outro: "Outro",
};

const requestTypeDescriptions: Record<RequestType, string> = {
  acesso: "Solicite uma cópia de todos os dados pessoais que tratamos sobre você.",
  correcao: "Solicite a correção de dados pessoais incompletos, inexatos ou desatualizados.",
  exclusao: "Solicite a exclusão dos dados pessoais tratados com seu consentimento.",
  portabilidade: "Solicite a transferência dos seus dados para outro fornecedor.",
  revogar_consentimento: "Revogue o consentimento para tratamento dos seus dados pessoais.",
  outro: "Outro tipo de solicitação relacionada à LGPD.",
};

export default function PrivacyRequestPage() {
  const navigate = useNavigate();
  const [requestType, setRequestType] = useState<RequestType | "">("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!requestType || !name || !email) return;

    setLoading(true);
    // Simula envio — substituir por chamada à API quando disponível
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);
    setSubmitted(true);
    toast.success("Solicitação enviada com sucesso!");
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-lg w-full text-center">
          <CardHeader>
            <CheckCircle2 className="mx-auto mb-2 h-12 w-12 text-primary" />
            <CardTitle>Solicitação enviada</CardTitle>
            <CardDescription>
              Recebemos sua solicitação e responderemos em até 15 dias úteis,
              conforme previsto na LGPD. O DPO da IndiQR entrará em contato
              através do e-mail informado.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <p>
              Protocolo: <span className="font-mono text-foreground">{crypto.randomUUID().slice(0, 8).toUpperCase()}</span>
            </p>
            <p className="mt-1">
              Guarde este protocolo para acompanhar sua solicitação.
            </p>
          </CardContent>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Voltar ao início
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-10">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Solicitação de Direitos (LGPD)</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Exerça seus direitos como titular de dados pessoais conforme a Lei
            Geral de Proteção de Dados.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dados da solicitação</CardTitle>
            <CardDescription>
              Preencha o formulário abaixo para exercer seus direitos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="request-type">Tipo de solicitação *</Label>
                <Select value={requestType} onValueChange={(v) => setRequestType(v as RequestType)}>
                  <SelectTrigger id="request-type">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.entries(requestTypeLabels) as [RequestType, string][]).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {requestType && (
                  <p className="text-xs text-muted-foreground">
                    {requestTypeDescriptions[requestType]}
                  </p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome completo *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone (opcional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição detalhada *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descreva detalhadamente sua solicitação..."
                  rows={5}
                  required
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Ao enviar, você declara que as informações fornecidas são
                verdadeiras. Responderemos em até 15 dias úteis conforme a LGPD.
              </p>

              <Button type="submit" disabled={loading || !requestType || !name || !email || !description} className="w-full">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {loading ? "Enviando..." : "Enviar solicitação"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          DPO: <a href="mailto:dpo@indiqr.com.br" className="underline hover:text-foreground">dpo@indiqr.com.br</a>
          {" · "}
          <a href="/privacidade/politica-de-privacidade.html" className="underline hover:text-foreground">
            Política de Privacidade
          </a>
        </p>
      </div>
    </div>
  );
}
